<template>
  <div class="recover-container">
    <div class="recover-box">
      <p class="recover-title">복구 코드를 입력해 주세요.</p>
      <input
        type="text"
        v-model="recoveryCode"
        placeholder="복구 코드 입력"
        class="recover-input"
      />
      <div class="message-area">
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        <p v-else-if="successMessage" class="success-message">
          {{ successMessage }}
        </p>
      </div>

      <div class="button-group">
        <button
          @click="recoverAccount"
          :disabled="loading"
          class="recover-button"
        >
          확인
        </button>
        <button @click="goBack" class="back-button">취소</button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import { db } from '@/firebase' // Firestore 인스턴스 import
  import {
    collection,
    query,
    where,
    getDocs,
    doc,
    setDoc,
    deleteDoc,
  } from 'firebase/firestore' // Firestore 쿼리 및 문서 함수 import
  import { useStore } from 'vuex' // Vuex store 사용

  const recoveryCode = ref('')
  const errorMessage = ref('')

  watch(recoveryCode, (newVal) => {
    if (newVal) {
      errorMessage.value = false
    }
  })

  const successMessage = ref('')
  const loading = ref(false)

  const router = useRouter()
  const store = useStore() // store 인스턴스 얻기

  const goBack = () => {
    router.go(-1)
  }

  const recoverAccount = async () => {
    errorMessage.value = ''
    successMessage.value = ''
    loading.value = true

    if (!recoveryCode.value) {
      errorMessage.value = '복구 코드를 입력해주세요.'
      loading.value = false
      return
    }

    try {
      const guestAccessToken = localStorage.getItem('accessToken')

      // Firestore에서 복구 코드가 일치하는 사용자 문서 찾기
      const usersRef = collection(db, 'users')
      const q = query(usersRef, where('recoveryCode', '==', recoveryCode.value))
      const querySnapshot = await getDocs(q)

      if (!querySnapshot.empty) {
        // 복구 코드가 일치하는 사용자 문서 발견
        const userDoc = querySnapshot.docs[0]
        const userData = userDoc.data()
        const accessToken = userDoc.id // 문서 ID가 액세스 코드입니다.

        console.log('복구 성공! 사용자 데이터:', userData)

        // localStorage에 액세스 코드 저장
        localStorage.setItem('accessToken', accessToken)

        // Vuex store에 사용자 정보 업데이트 (initializeAuth 액션 호출 또는 직접 commit)
        // initializeAuth 액션을 호출하여 전체 로딩 및 설정 프로세스를 따르는 것이 일관적입니다.
        await store.dispatch('initializeAuth')

        successMessage.value = '계정이 성공적으로 복구되었습니다.'

        if (guestAccessToken && guestAccessToken !== accessToken) {
          try {
            await deleteDoc(doc(db, 'users', guestAccessToken))
            console.log('손님 사용자 문서 삭제 완료')
          } catch (e) {
            console.warn('손님 사용자 문서 삭제 실패:', e)
          }
        }

        // 메인 화면으로 리다이렉트 (딜레이 추가)
        setTimeout(() => {
          router.replace('/IChingDaily') // 메인 화면 경로
        }, 2000) // 성공 메시지를 볼 시간 제공
      } else {
        // 복구 코드가 일치하는 사용자 문서 없음
        errorMessage.value = '유효하지 않은 복구코드입니다.'
      }
    } catch (error) {
      console.error('계정 복구 중 오류 발생:', error)
      errorMessage.value =
        '계정 복구 중 오류가 발생했습니다. 다시 시도해주세요.'
    } finally {
      loading.value = false
    }
  }
</script>

<style scoped>
  /* IntroIChingDaily.vue의 스타일을 참고하여 센터 정렬 및 요소 스타일 적용 */
  .recover-container {
    display: flex;
    flex-direction: column;
    align-items: center; /* 수평 센터 정렬 */
    justify-content: center; /* 수직 센터 정렬 */
    min-height: 100vh; /* 최소 높이를 뷰포트 높이로 설정하여 수직 센터 정렬 효과 */
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    padding: 1.2rem;
    background-color: #f5f5f5; /* 배경색 (SetupUserData와 통일) */
  }

  .recover-box {
    display: flex;
    flex-direction: column;
    align-items: center; /* recover-box 내부 요소 센터 정렬 */
    gap: 1.5rem; /* 요소 간 간격 */
    padding: 1rem;
    background: white;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    width: 90%; /* 너비 설정 */
    text-align: center; /* 텍스트 센터 정렬 */
  }

  .recover-title {
    font-size: var(--font-size-11); /* 폰트 크기 (IntroIChingDaily 참고) */
    margin-bottom: 1rem;
    color: #333;
  }

  .recover-input {
    padding: 0.8rem;
    background-color: white;
    color: black;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: var(--font-size-10); /* 폰트 크기 */
    width: 100%; /* 부모 요소 너비에 맞춤 */
    box-sizing: border-box; /* padding 포함하여 너비 계산 */
    text-align: center; /* 입력 텍스트 센터 정렬 */
  }

  .button-group {
    display: flex;
    justify-content: center; /* Center the button group */
    gap: 10px; /* Space between buttons */
  }

  .recover-button {
    background-color: #4caf50; /* 배경색 (IntroIChingDaily 버튼 참고) */
    color: white;
    padding: 0.7rem 1.5rem;
    border: none;
    border-radius: 6px; /* 모서리 둥글게 */
    cursor: pointer;
    font-size: var(--font-size-10);
    transition: background-color 0.3s ease; /* 호버 효과 */
  }

  .recover-button:hover {
    background-color: #45a049; /* 호버 시 색상 변경 */
  }

  .recover-button:disabled {
    background-color: #888;
    cursor: not-allowed;
  }

  .back-button {
    padding: 0.7rem 1.5rem;
    font-size: var(--font-size-10);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    background-color: #f44336; /* Red */
    color: white;
  }
  .back-button:hover {
    background-color: #da190b; /* Darker red */
  }

  .message-area {
    height: 20px; /* 항상 공간 확보 */
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-bottom: 10px;
    overflow-x: hidden; /* 내용이 넘칠 경우 숨김 처리 */
  }

  .message-area p {
    margin: 0;
    padding: 0;
    line-height: 1.2;
  }

  .error-message {
    color: red;
    font-size: var(--font-size-095);
  }

  .success-message {
    font-size: var(--font-size-095);
    color: #45a049; /* 성공 메시지 색상 */
  }
</style>
